// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { SearchRequest } from '@medplum/core';
import { parseSearchRequest } from '@medplum/core';

export type ThreadBucket = 'unread' | 'sent' | 'all';

export const THREAD_BUCKETS: readonly ThreadBucket[] = ['unread', 'sent', 'all'] as const;

export interface NormalizeCommunicationSearchOptions {
  search?: string;
  defaultSort?: string;
  defaultStatus?: string;
  defaultCount?: string;
  defaultTotal?: string;
  defaultBucket?: ThreadBucket;
}

export interface NormalizeCommunicationSearchResult {
  normalizedSearch: string;
  parsedSearch: SearchRequest;
  bucket: ThreadBucket;
}

function parseBucket(value: string | null, fallback: ThreadBucket): ThreadBucket {
  if (value && (THREAD_BUCKETS as readonly string[]).includes(value)) {
    return value as ThreadBucket;
  }
  return fallback;
}

export function normalizeCommunicationSearch({
  search = '',
  defaultSort = '-_lastUpdated',
  defaultStatus = 'in-progress',
  defaultCount = '20',
  defaultTotal = 'accurate',
  defaultBucket = 'unread',
}: NormalizeCommunicationSearchOptions): NormalizeCommunicationSearchResult {
  const params = new URLSearchParams(search || `_sort=${defaultSort}`);

  const bucket = parseBucket(params.get('bucket'), defaultBucket);
  params.delete('bucket');

  const entries = Array.from(params.entries());

  const addIfMissing = (key: string, value: string | undefined): void => {
    if (value && !params.has(key)) {
      entries.push([key, value]);
    }
  };

  addIfMissing('_sort', defaultSort);
  addIfMissing('status', defaultStatus);
  addIfMissing('_count', defaultCount);
  addIfMissing('_total', defaultTotal);

  const fhirEntries = [['bucket', bucket] as [string, string], ...entries];
  const normalizedSearch = new URLSearchParams(fhirEntries).toString();

  return {
    normalizedSearch,
    parsedSearch: parseSearchRequest(`Communication?${new URLSearchParams(entries).toString()}`),
    bucket,
  };
}
